import React from "react";
import { Box, CircularProgress, CssBaseline, Typography } from "@mui/material";
import logo from "../assets/logo-mark.png";
import productImage from "../assets/4.webp";
import { colors } from "../styles/tokens";

export const GOLD = colors.gold;

// Shared look for the sign-in pages (/login, /superadmin, /register, ...). Colours, corners and focus come from the
// theme; these only set the taller sign-in sizes.
export const fieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": { minHeight: "52px" },
};

export const submitSx = {
  width: "100%",
  height: "52px",
  "&.Mui-disabled": { backgroundColor: colors.gold, color: "#ffffff", opacity: 0.6 },
};

export const helperTextSx = {
  fontSize: "15px",
  fontWeight: 500,
  lineHeight: "24px",
};

/** Google / Microsoft sign-in buttons. */
export const oauthButtonSx = {
  flex: 1,
  fontWeight: 500,
  fontSize: "16px",
  color: colors.ink,
  borderColor: colors.inputBorder,
  "&:hover": { borderColor: colors.ink, backgroundColor: "#FAFAFA" },
};

/** Muted line of text under a heading or toggle. */
export const mutedTextSx = { color: colors.muted, textAlign: "left" };

/** Button content: the label, or a spinner while the sign-in is running. */
export const SubmitLabel = ({ busy, children }) =>
  busy ? (
    <CircularProgress size={24} color="inherit" aria-label="Signing in" />
  ) : (
    <Typography sx={{ fontWeight: 600, fontSize: "18px" }}>{children}</Typography>
  );

const titleSx = {
  fontWeight: 700,
  fontSize: { xs: "28px", sm: "34px" },
  lineHeight: 1.2,
};

/** Page heading: plain lead text followed by the gold part, e.g. "Log in to" + "VAVETEK.AI". */
export const AuthTitle = ({ lead, accent }) => (
  <Typography component="h2" sx={{ ...titleSx, textAlign: "left", color: colors.ink }}>
    {lead && <>{lead} </>}
    <Box component="span" sx={{ color: colors.gold }}>{accent}</Box>
  </Typography>
);

/**
 * Two-column sign-in layout: the form on the left, the product image (with optional `aside` content) on the right.
 * On phones the image is hidden and the form fills the width.
 */
export default function AuthLayout({ children, aside }) {
  return (
    // On desktop the page is exactly one screen tall: the form column scrolls on its own (with space kept for its
    // scrollbar, so switching between forms of different heights doesn't shift it) and the image stays put.
    <Box sx={{ height: { md: "100vh" }, minHeight: "100vh", display: "flex", alignItems: "stretch", width: "100%", margin: 0 }}>
      <CssBaseline />

      <Box
        sx={{
          flex: 5,
          display: "flex",
          // Top-aligned rather than centred, so the heading stays put when the form below it changes height.
          alignItems: "flex-start",
          justifyContent: "center",
          overflowY: { md: "auto" },
          scrollbarGutter: { md: "stable" },
          px: { xs: 2, sm: 4 },
          pt: { xs: 3, md: 5 },
          pb: 4,
          backgroundColor: colors.surface,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: "461px", textAlign: "left" }}>
          <Box display="flex" alignItems="center" mb={2} gap={1.5}>
            <Box component="img" src={logo} alt="" sx={{ width: 44, height: 44 }} />
            <Typography component="h1" sx={{ fontWeight: 700, fontSize: "20px", letterSpacing: "1px", color: colors.ink }}>
              VAVETEK AI
            </Typography>
          </Box>
          {children}
        </Box>
      </Box>

      <Box
        sx={{
          display: { xs: "none", md: "block" },
          flex: 5,
          background: `url(${productImage}) center center/cover no-repeat`,
        }}
      >
        {aside}
      </Box>
    </Box>
  );
}
